import React, { useState } from 'react'
import { range, round, clamp } from 'lodash-es'
import { useDrag } from '../../lib/hooks/useDrag'
import { useParentSize } from '../../lib/hooks/useParentSize'
import { scaleLinear } from 'd3-scale'
import { AnimatedCircle } from '../AnimatedCircle'
import { ShadowFilter } from './ShadowFilter'
import { useConstantId } from '../../lib/hooks/useConstantId'

const ANIMATION_DURATION_SEC = 0.3
const PLACEHOLDER_RADIUS = 4
const PLACEHOLDER_TAPPABLE_RADIUS = 15
const INNER_RADIUS = 12
const OUTER_RADIUS = 12
const HOVER_SCALE = 1
const DRAGGING_SCALE = 1.2
const DRAGGING_OUTER_RADIUS = OUTER_RADIUS * DRAGGING_SCALE
const HEIGHT = 2 * DRAGGING_OUTER_RADIUS + 6 // 6 is for the biggest strokeWidth
const CY = HEIGHT / 2
const GRAY = 'var(--gray500)'
const BLUE = 'var(--blue500)'
const WHITE = '#fff'
const LINE_STROKE_WIDTH = 2

interface Props {
  className?: string
  stepsCounter: number
  selectedStep?: number
  onChange?: (value: number) => void
}

export const SliderStep: React.FC<Props> = ({
  className = '',
  stepsCounter,
  selectedStep = 0,
  onChange,
}) => {
  const [ref, { width }] = useParentSize<HTMLDivElement>()

  const minStep = 0
  const maxStep = stepsCounter - 1

  const offset = DRAGGING_OUTER_RADIUS
  const minStepWidth = 50
  const stepWidth = minStepWidth * maxStep < width ? minStepWidth : (width - offset * 2) / maxStep
  const sliderWidth = stepWidth * maxStep

  const xScale = scaleLinear<number, number>().domain([minStep, maxStep]).range([0, sliderWidth])
  const [isMoving, setIsMoving] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const onDrag = ({ deltaX }: { deltaX: number }) => {
    const currentX = xScale(selectedStep)
    const selectedX = currentX + deltaX
    const newSelectedStep = clamp(round(xScale.invert(selectedX)), minStep, maxStep)
    onChange(newSelectedStep)
  }
  const onDragStart = () => setIsMoving(true)
  const onDragEnd = () => setIsMoving(false)
  const [selectedRef] = useDrag(onDrag, onDragStart, onDragEnd)

  const shadowFilterId = useConstantId('-slider-step-')

  if (width === 0) return <div className={`${className}`} ref={ref} />

  return (
    <div className={`${className}`} ref={ref}>
      <svg
        className="overflow-visible"
        width={sliderWidth + offset * 2}
        height={HEIGHT}
        x={0}
        y={0}
      >
        <g transform={`translate(${offset})`}>
          <line
            x1={0}
            y1={CY}
            x2={sliderWidth}
            y2={CY}
            stroke={isMoving ? BLUE : GRAY}
            strokeWidth={LINE_STROKE_WIDTH}
            style={{ transition: 'stroke 0.2s' }}
          />

          <g className="-step-circles">
            {range(stepsCounter).map((step) => (
              <g key={step}>
                <circle
                  className="-visible-placeholder"
                  cx={xScale(step)}
                  cy={CY}
                  r={PLACEHOLDER_RADIUS}
                  fill={isMoving ? BLUE : GRAY}
                  style={{ transition: 'fill 0.2s' }}
                />
                <circle
                  className="-tappable-invidible-placeholder pointer"
                  cx={xScale(step)}
                  cy={CY}
                  r={PLACEHOLDER_TAPPABLE_RADIUS}
                  fill="transparent"
                  onClick={() => {
                    onChange(step)
                    setIsMoving(true)
                    setTimeout(() => setIsMoving(false), ANIMATION_DURATION_SEC * 1000)
                  }}
                />
              </g>
            ))}
            <g
              className="-animated-slider-circle"
              ref={selectedRef}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <ShadowFilter
                id={shadowFilterId}
                dx={0}
                dy={0}
                stdDeviation={2.3}
                floodColor="rgba(0, 0, 0, .5)"
              />
              <AnimatedCircle
                className=""
                cx={xScale(selectedStep)}
                cy={CY}
                r={isMoving ? DRAGGING_OUTER_RADIUS : OUTER_RADIUS}
                fill={'WHITE'}
                animationDurationSec={ANIMATION_DURATION_SEC}
                filter={`url(#${shadowFilterId})`}
              />
              <AnimatedCircle
                className=""
                cx={xScale(selectedStep)}
                cy={CY}
                r={
                  isMoving
                    ? INNER_RADIUS * DRAGGING_SCALE // inner radius grows more than the outer
                    : isHovered
                    ? INNER_RADIUS * HOVER_SCALE
                    : INNER_RADIUS
                }
                fill={WHITE}
                animationDurationSec={ANIMATION_DURATION_SEC}
              />
            </g>
          </g>
        </g>
      </svg>
    </div>
  )
}
